// 🖋️ Ink 듣기 — 읽는 화면의 「듣기」 단추 줄(ink/chapters/show)이 쓰는 작은 스크립트.
//
// 독자 기기의 읽어 주기 목소리(Web Speech API 의 speechSynthesis)로 이 회의 본문을 처음부터 읽힌다.
//   · 읽을 글은 화면에 이미 나와 있는 본문 문단에서 그 자리에서 읽어 온다 — 서버에서 따로 받아 오지 않는다
//   · 단추 줄은 서버가 숨긴 채로 그린다. 이 기기가 한국어로 읽을 수 있다는 것을 확인한 뒤에만 보인다
//   · 외부 스크립트 · 외부 음성 서비스를 쓰지 않고, 누가 무엇을 들었는지 적어 두지 않는다
//
// 멈춤은 pause() 가 아니라 cancel() 로 한다 — pause() 가 듣지 않는 기기가 있다(안드로이드 크롬).
// 그래서 「이어 듣기」 는 멈춘 토막의 처음부터 다시 읽는다.

import { chunksOf } from './listen-text';

type State = 'idle' | 'playing' | 'paused';
type Piece = { el: HTMLElement; text: string; rest: number };

const RATE_KEY = 'ink.listen.rate';
const REST_AFTER_RULE = 700; // 가름줄 뒤에 쉬는 길이(ms)
const LABELS: Record<State, string> = { idle: '듣기', playing: '멈춤', paused: '이어 듣기' };

const bar = document.querySelector<HTMLElement>('[data-ink-listen]');
const body = document.querySelector<HTMLElement>('[data-ink-body]');
const synth: SpeechSynthesis | undefined = window.speechSynthesis;

if (bar && body && synth && typeof SpeechSynthesisUtterance !== 'undefined') {
    start(bar, body, synth);
}

function koreanVoice(synth: SpeechSynthesis): SpeechSynthesisVoice | null {
    const voices = synth.getVoices().filter((voice) => /^ko([-_]|$)/i.test(voice.lang));
    return voices.find((voice) => voice.default) ?? voices[0] ?? null;
}

// 본문 문단 <p> 만 읽는다. 옮긴이가 밝히는 말(aside.ink-note)은 건너뛰고, <br> 과 가름줄 <hr> 은 쉬는 자리로 삼는다.
function collect(body: HTMLElement): Piece[] {
    const pieces: Piece[] = [];
    let rest = 0;

    body.querySelectorAll<HTMLElement>('p, hr').forEach((el) => {
        if (el.closest('.ink-note')) return;
        if (el.tagName === 'HR') {
            rest = REST_AFTER_RULE;
            return;
        }

        const copy = el.cloneNode(true) as HTMLElement;
        copy.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
        (copy.textContent ?? '').split('\n').forEach((line) => {
            chunksOf(line).forEach((text) => {
                pieces.push({ el, text, rest });
                rest = 0;
            });
        });
    });

    return pieces;
}

function start(bar: HTMLElement, body: HTMLElement, synth: SpeechSynthesis): void {
    const toggle = bar.querySelector<HTMLButtonElement>('[data-ink-listen-toggle]');
    const restart = bar.querySelector<HTMLButtonElement>('[data-ink-listen-restart]');
    const rates = Array.from(bar.querySelectorAll<HTMLButtonElement>('[data-ink-listen-rate]'));
    if (!toggle) return;

    const pieces = collect(body);
    if (pieces.length === 0) return;

    const calm = window.matchMedia('(prefers-reduced-motion: reduce)');
    let voice: SpeechSynthesisVoice | null = null;
    let state: State = 'idle';
    let at = 0;
    let turn = 0; // 읽기를 끊을 때마다 올린다 — 끊긴 토막의 뒤늦은 신호를 가려낸다
    let timer = 0;
    let lit: HTMLElement | null = null;
    let rate = 1;

    try {
        const kept = Number(window.localStorage.getItem(RATE_KEY));
        if (rates.some((button) => Number(button.dataset.inkListenRate) === kept)) rate = kept;
    } catch {
        // 기억해 두는 곳을 못 쓰는 환경 — 보통 빠르기로 읽는다
    }

    function light(el: HTMLElement | null): void {
        if (lit === el) return;
        lit?.classList.remove('ink-listen-now');
        lit = el;
        if (!el) return;
        el.classList.add('ink-listen-now');
        const box = el.getBoundingClientRect();
        if (box.top < 0 || box.bottom > window.innerHeight) {
            el.scrollIntoView({ behavior: calm.matches ? 'auto' : 'smooth', block: 'center' });
        }
    }

    function show(next: State): void {
        state = next;
        toggle!.textContent = LABELS[next];
        bar.dataset.inkListenState = next;
    }

    function showRate(): void {
        rates.forEach((button) => {
            button.setAttribute('aria-pressed', String(Number(button.dataset.inkListenRate) === rate));
        });
    }

    function hush(): void {
        turn += 1;
        window.clearTimeout(timer);
        synth.cancel();
    }

    function finish(): void {
        hush();
        at = 0;
        light(null);
        show('idle');
    }

    function speak(): void {
        if (at >= pieces.length) {
            finish();
            return;
        }

        const mine = turn;
        const piece = pieces[at];
        const say = new SpeechSynthesisUtterance(piece.text);
        say.lang = 'ko-KR';
        if (voice) say.voice = voice;
        say.rate = rate;
        say.onend = () => {
            if (mine !== turn) return;
            at += 1;
            const rest = at < pieces.length ? pieces[at].rest : 0;
            if (rest > 0) timer = window.setTimeout(() => mine === turn && speak(), rest);
            else speak();
        };
        say.onerror = () => {
            if (mine !== turn) return;
            hush();
            show('paused');
        };

        light(piece.el);
        synth.speak(say);
    }

    function play(): void {
        hush();
        show('playing');
        speak();
    }

    function pause(): void {
        hush();
        show('paused');
    }

    toggle.addEventListener('click', () => (state === 'playing' ? pause() : play()));

    restart?.addEventListener('click', () => {
        at = 0;
        play();
    });

    rates.forEach((button) => {
        button.addEventListener('click', () => {
            rate = Number(button.dataset.inkListenRate) || 1;
            showRate();
            try {
                window.localStorage.setItem(RATE_KEY, String(rate));
            } catch {
                // 기억하지 못해도 읽기는 된다
            }
            if (state === 'playing') play();
        });
    });

    // 화면을 떠나거나 다른 회로 갈 때 멈춘다 — 멈추지 않으면 다음 화면에서도 앞 회의 소리가 이어진다.
    window.addEventListener('pagehide', () => {
        if (state === 'playing') pause();
        else synth.cancel();
    });

    // 한국어 목소리가 있을 때만 단추 줄을 보인다. 목소리 목록이 늦게 차는 브라우저가 있어 기다렸다가 다시 본다.
    let tries = 0;
    function reveal(): void {
        if (!bar.hidden) return;
        voice = koreanVoice(synth);
        if (!voice) return;
        synth.cancel(); // 앞 화면에서 남은 소리가 있으면 지운다
        showRate();
        show('idle');
        bar.hidden = false;
    }
    function wait(): void {
        reveal();
        tries += 1;
        if (bar.hidden && tries < 12) window.setTimeout(wait, 250);
    }

    synth.addEventListener?.('voiceschanged', reveal);
    wait();
}
